// TechnicianReportPDF.js
import {
  Document,
  Font,
  Image,
  Link,
  Page,
  Rect,
  Stop,
  StyleSheet,
  Svg,
  Text,
  View,
  LinearGradient,
  Defs,
} from "@react-pdf/renderer";
import React from "react";
import facebook from '../../../src/images/homepage/footer/facebook.png';
import instagram from '../../../src/images/homepage/footer/instagram.png';
import twitter from '../../../src/images/homepage/footer/twitter.png';
import youtube from '../../../src/images/homepage/footer/youtube.png';
import { default as footerLogo, default as logo } from '../../../src/images/pdf/nav.png';
import logoDark from '../../../src/images/pdf/nav-dark.png';
import car from "../../../src/images/progress/car.png";

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

Font.register({
  family: 'Poppins',
  src: require('../../../src/font/Poppins-Regular.ttf'),
});

Font.register({
  family: 'PoppinsS',
  src: require('../../../src/font/Poppins-SemiBold.ttf'),
});

Font.register({
  family: 'Clash',
  src: require('../../../src/font/ClashGrotesk.ttf'),
});

// ---------------------------------------------------------------------------
// CarvetPro palette — identical to the PDI/Used Car report templates (same
// design system, same component library below).
// ---------------------------------------------------------------------------

const LIME = '#ADD144';
const LIME_TINT = '#F0FFC5';
const DARK = '#1F1F1F';
const CARD_BG = '#E9E8E8';
const CARD_BORDER = '#DDDDDD';
const TEXT_BODY = '#333333';
const TEXT_LABEL = '#777777';
const TEXT_VALUE = '#1F1F1F';

const STATUS = {
  excellent: { label: 'EXCELLENT', bg: '#E8F5E1', color: '#1F8A3D' },
  average: { label: 'AVERAGE', bg: '#FFF6D9', color: '#B98900' },
  poor: { label: 'POOR', bg: '#FFF1DE', color: '#D97706' },
  critical: { label: 'CRITICAL', bg: '#FDE8E8', color: '#DC2626' },
  attention: { label: 'NEEDS ATTENTION', bg: '#FFF1DE', color: '#D97706' },
};

const tierFromScore = (score) => {
  if (score >= 90) return STATUS.excellent;
  if (score >= 75) return STATUS.average;
  if (score >= 60) return STATUS.poor;
  return STATUS.critical;
};

// Title-cased label for prose use (e.g. "53.41% · Average"), always derived
// from the same 4-tier tierFromScore boundaries as the badge color — kept as
// one source of truth so the label and badge tier can never disagree.
const tierLabelTitleCase = (score) => {
  const label = tierFromScore(score).label;
  return label.charAt(0) + label.slice(1).toLowerCase();
};

const scoreDescription = (score) => {
  if (score >= 90) return 'This score signifies the vehicle is mechanically ready for the road and meets the highest standards.';
  if (score >= 75) return 'This score indicates the vehicle is in good condition with only minor concerns.';
  if (score >= 60) return 'This score indicates the vehicle has moderate mechanical concerns but is generally functional.';
  return 'This score reflects significant mechanical concerns, requiring attention before this trip.';
};

const zoneTier = (value) => (value ? STATUS.attention : STATUS.excellent);

// There's no dedicated short booking-reference field anywhere in the data
// model (confirmed against the Service schema) — the Mongo _id (24 hex
// chars) is genuinely the only identifier that exists. Shown as the last 8
// characters (the most-random tail of an ObjectId, so distinct bookings
// stay distinguishable) with a # prefix, keeping the display at ≤10 chars.
const shortBookingId = (id) => (id ? `#${String(id).slice(-8).toUpperCase()}` : null);

// ---------------------------------------------------------------------------
// Styles — identical to the PDI/Used Car report templates
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    backgroundColor: "#ffffff",
    fontFamily: 'Poppins',
    paddingBottom: 40,
  },
  darkPage: {
    fontSize: 10,
    backgroundColor: DARK,
    fontFamily: 'Poppins',
  },

  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 36,
    paddingTop: 24,
    paddingBottom: 12,
  },
  pageHeaderLabel: {
    fontSize: 9,
    color: TEXT_LABEL,
    fontFamily: 'PoppinsS',
  },
  pageHeaderLogo: { width: 90, height: 26 },

  body: { paddingHorizontal: 36 },

  h1: { fontFamily: 'Clash', fontSize: 26, color: DARK, marginBottom: 4, maxWidth: '75%' },
  h2: { fontFamily: 'Clash', fontSize: 16, color: DARK, marginBottom: 8 },
  subtitle: { fontSize: 10, color: TEXT_LABEL, marginBottom: 18 },

  card: {
    borderWidth: 1,
    borderColor: CARD_BORDER,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  cardGray: {
    backgroundColor: CARD_BG,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },

  label: { fontSize: 8, color: TEXT_LABEL, fontFamily: 'Poppins', marginBottom: 2 },
  value: { fontSize: 11, color: TEXT_VALUE, fontFamily: 'PoppinsS' },

  row: { flexDirection: 'row' },
  grid2: { flexDirection: 'row', flexWrap: 'wrap' },
  gridCell: { width: '50%', marginBottom: 12, paddingRight: 12 },

  divider: { borderBottomWidth: 1, borderBottomColor: CARD_BORDER, marginVertical: 10 },

  badge: {
    borderRadius: 5,
    paddingHorizontal: 8,
    paddingVertical: 5,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 7,
    fontFamily: 'PoppinsS',
    letterSpacing: 0.3,
  },

  highlightRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 8,
  },
  highlightDot: {
    width: 8, height: 8, borderRadius: 4, marginRight: 10,
  },
  highlightText: { fontSize: 10, color: TEXT_BODY, flex: 1 },

  scoreCard: {
    borderWidth: 1,
    borderColor: CARD_BORDER,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  scoreCardHeadRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  scoreCardTitle: { fontSize: 13, fontFamily: 'PoppinsS', color: DARK },
  scoreCardDesc: { fontSize: 9, color: TEXT_LABEL },

  photoGrid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6 },
  photoCell: { width: '50%', paddingHorizontal: 6, marginBottom: 16 },
  photoCell3: { width: '33.33%', paddingHorizontal: 6, marginBottom: 16 },
  photoImg: { width: '100%', height: 100, borderRadius: 8, objectFit: 'cover' },
  photoCaption: { fontSize: 8, color: TEXT_LABEL, marginTop: 4, fontFamily: 'PoppinsS' },

  zoneRow: {
    borderBottomWidth: 1, borderBottomColor: '#EFEFEF', paddingVertical: 11,
  },
  zoneLabel: { fontSize: 9.5, color: TEXT_BODY, fontFamily: 'PoppinsS' },
  zoneDetail: { fontSize: 8, color: TEXT_LABEL, marginTop: 2 },

  // Reason text (why a zone is flagged): no box, no fill, no border — the
  // "Reason" label carries the same amber as the NEEDS ATTENTION badge
  // (#D97706), the detail text is plain black, both bold, one clean line.
  reasonBox: {
    marginTop: 3,
  },
  reasonLine: { fontSize: 8.5 },
  reasonLabel: { fontSize: 8.5, fontFamily: 'PoppinsS', color: '#D97706' },
  reasonText: { fontSize: 8.5, fontFamily: 'PoppinsS', color: '#1F1F1F' },

  // Technician's Remarks: one row per remark, a hairline divider between
  // rows (same rhythm as the zone rows). Text flexes to the remaining width
  // on the left; the photo, when present, is a fixed 120x90 thumbnail on the
  // right, cropped to fill via objectFit 'cover' (supported by react-pdf's
  // <Image>). No photo -> the text spans the full row width, nothing
  // reserved. No leading numerals — the row/divider structure organises the
  // list and order is preserved by the array order. 16pt vertical padding on
  // every row. Each row is one wrap={false} unit so a remark and its photo
  // never split across a page.
  remarkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: '#EFEFEF',
  },
  remarkRowLast: { borderBottomWidth: 0 },
  remarkText: { fontSize: 9, color: TEXT_BODY, flex: 1, paddingRight: 14, lineHeight: 1.45 },
  remarkPhoto: {
    width: 120,
    height: 90,
    borderRadius: 6,
    objectFit: 'cover',
    borderWidth: 1,
    borderColor: CARD_BORDER,
  },

  legendRow: { flexDirection: 'row', marginTop: 22 },
  legendCell: { flex: 1, paddingRight: 10 },
  legendBadge: { alignSelf: 'flex-start', marginBottom: 4 },
  legendDesc: { fontSize: 7.5, color: TEXT_LABEL },

  footnote: {
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: CARD_BORDER,
    borderRadius: 8,
    padding: 10,
    marginTop: 12,
  },
  footnoteText: { fontSize: 8, color: TEXT_LABEL },

  footerContainer: {
    backgroundColor: DARK,
    color: '#FFFFFF',
    padding: 24,
    marginTop: 30,
  },
  contentWrapper: { flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' },
  sectionss: { width: '23%', marginBottom: 20 },
  // Sized to match the tagline text block directly below it, same measured
  // value used on both the PDI and Used Car reports (267x76 asset ratio).
  footerLogo: { width: 121, height: 34, marginBottom: 10 },
  description: { fontSize: 9, color: '#D1D1D1', marginBottom: 15 },
  socialIcons: { flexDirection: 'row', gap: 5 },
  iconWrapper: { padding: 4 },
  socialIcon: { width: 13, height: 13 },
  sectionHeading: { fontSize: 10, fontFamily: 'PoppinsS', marginBottom: 8, color: LIME },
  listItem: { fontSize: 8.5, marginBottom: 5, color: '#EDEDED' },
  dividers: { borderTop: '1px solid #3A3A3A', marginVertical: 10 },
  copyright: { fontSize: 7.5, textAlign: 'center', color: '#AAAAAA' },
});

// ---------------------------------------------------------------------------
// Small components — identical to the PDI/Used Car report templates
// ---------------------------------------------------------------------------

const PageHeader = ({ pageLabel }) => (
  <View style={styles.pageHeader} fixed>
    <Text style={styles.pageHeaderLabel}>{pageLabel || 'Pre-Trip Inspection Report'}</Text>
    <Image style={styles.pageHeaderLogo} src={logoDark} />
  </View>
);

const Badge = ({ tier }) => (
  <View style={[styles.badge, { backgroundColor: tier.bg }]}>
    <Text style={[styles.badgeText, { color: tier.color }]}>{tier.label}</Text>
  </View>
);

const Legend2 = () => (
  <View wrap={false} style={styles.legendRow}>
    <View style={styles.legendCell}>
      <View style={[styles.badge, styles.legendBadge, { backgroundColor: STATUS.excellent.bg }]}>
        <Text style={[styles.badgeText, { color: STATUS.excellent.color }]}>EXCELLENT</Text>
      </View>
      <Text style={styles.legendDesc}>No issue reported for this zone</Text>
    </View>
    <View style={styles.legendCell}>
      <View style={[styles.badge, styles.legendBadge, { backgroundColor: STATUS.attention.bg }]}>
        <Text style={[styles.badgeText, { color: STATUS.attention.color }]}>NEEDS ATTENTION</Text>
      </View>
      <Text style={styles.legendDesc}>Technician flagged an issue on this zone (see description)</Text>
    </View>
  </View>
);

const ZONE_BADGE_GUTTER = 104;

// `reason` (optional) separates "why this is flagged" from `value` when a
// caller also has other descriptive text that isn't itself the reason (e.g.
// Tyres' brand/variant/size string) — `value` still renders as plain detail
// text in that case, and `reason` gets the amber callout treatment on its
// own line below it. When `reason` isn't given, `value` doubles as the
// reason text for every other zone type (the common case).
const ZoneRow = ({ label, value, flagged, reason }) => {
  const t = flagged === undefined ? zoneTier(value) : (flagged ? STATUS.attention : STATUS.excellent);
  const isFlagged = flagged === undefined ? !!value : flagged;
  const reasonText = reason !== undefined ? reason : (typeof value === 'string' ? value : '');
  const showReasonBox = isFlagged && !!reasonText;
  const showPlainDetail = typeof value === 'string' && !!value && (reason !== undefined || !showReasonBox);
  return (
    <View wrap={false} style={[styles.zoneRow, { position: 'relative', paddingRight: ZONE_BADGE_GUTTER }]}>
      <Text style={styles.zoneLabel}>{label}</Text>
      {showPlainDetail && <Text style={styles.zoneDetail}>{value}</Text>}
      {showReasonBox && (
        <View style={styles.reasonBox}>
          <Text style={styles.reasonLine}>
            <Text style={styles.reasonLabel}>Reason: </Text>
            <Text style={styles.reasonText}>{reasonText}</Text>
          </Text>
        </View>
      )}
      <View style={{ position: 'absolute', top: 0, bottom: 0, right: 0, justifyContent: 'center' }}>
        <Badge tier={t} />
      </View>
    </View>
  );
};

const ZoneGrid = ({ zones }) => {
  const pairs = [];
  for (let i = 0; i < zones.length; i += 2) pairs.push([zones[i], zones[i + 1]]);
  return (
    <View>
      {pairs.map((pair, i) => (
        <View style={{ flexDirection: 'row' }} key={i} wrap={false}>
          <View style={{ width: '50%', paddingRight: 10 }}>
            <ZoneRow label={pair[0][0]} value={pair[0][1]} />
          </View>
          <View style={{ width: '50%', paddingRight: 10 }}>
            {pair[1] && <ZoneRow label={pair[1][0]} value={pair[1][1]} />}
          </View>
        </View>
      ))}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PreTripPDF = ({ reportData, serviceData, carImageUrl }) => {

  // ---- Deduction / score math (kept identical to the existing live logic
  // in the pre-redesign preTripPdf.jsx). Pre-Trip only ever checked and
  // scored 5 categories — Engine Performance, System Observations, Core
  // Mechanical, Fluid Level, and Tyres. Body & Paint, Panel Gap, and every
  // Interior/Infotainment category were commented out of BOTH the display
  // and the score calculation in production before this redesign — this
  // isn't a display gap, it's the actual product scope (a quick mechanical
  // readiness check, not a cosmetic inspection), confirmed and kept as-is
  // per explicit decision, not restored. Weights are Pre-Trip's own —
  // roughly 3x PDI's, since far fewer categories carry the whole score. ----

  function calculateTotalDeductionFromConditions(conditions, weight) {
    const valid = conditions.filter((c) => Boolean(c));
    return valid.length * weight;
  }
  const enginePerformance = [
    reportData?.BatteryEnergy < 11.5,
    reportData?.LoadEngine > 35,
    reportData?.idleRPM > 1400,
  ];
  const deductionPerCondition = 2.5;
  const totalDeductionEngine = calculateTotalDeductionFromConditions(enginePerformance, deductionPerCondition);

  function calculateTotalDeductionForTrueStates(states, weight) {
    const trueStates = states.filter((s) => s === true);
    return trueStates.length * weight;
  }
  const systemObservations = [
    !reportData?.isToggledEngineCrack, !reportData?.isToggledWarning, !reportData?.isToggledSuspicious,
    !reportData?.isToggledSmoke, !reportData?.isToggledGearshift, !reportData?.isToggledBreak,
    !reportData?.isToggledSteering, !reportData?.isToggledHandbreak, !reportData?.isToggledReverse,
    !reportData?.isToggledSmooth,
  ];
  const deductionPerTrueState = 2.8409;
  const totalDeductionSystemObservations = calculateTotalDeductionForTrueStates(systemObservations, deductionPerTrueState);

  function calculateTotalSeatDeductionForTrueStates(states, weight) {
    const valid = states.filter((s) => s);
    return valid.length * weight;
  }
  const tyresStates = [
    reportData?.selectedConditionFT, reportData?.selectedConditionRTN, reportData?.selectedConditionRLN,
    reportData?.selectedConditionFLNT, reportData?.selectedConditionSparen,
  ];
  const deductionPerSeatValidState = 2.5;
  const totalDeductionTyres = calculateTotalSeatDeductionForTrueStates(tyresStates, deductionPerSeatValidState);

  function calculateTotalCoreDeductionForTrueStates(states, weight) {
    const valid = states.filter((s) => s && s !== 'Others');
    return valid.length * weight;
  }
  const carsCoreMechanicalStates = [
    reportData?.selectedConditionCarsCore2, reportData?.selectedConditionCarsCore3, reportData?.selectedConditionCarsCore1,
    reportData?.selectedConditionCarsCore11, reportData?.selectedConditionCarsCore4, reportData?.selectedConditionCarsCore5,
    reportData?.selectedConditionCarsCore6, reportData?.selectedConditionCarsCore7, reportData?.selectedConditionCarsCore8,
    reportData?.selectedConditionCarsCore9, reportData?.selectedConditionCarsCore10, reportData?.selectedConditionCarsCore12,
  ];
  const deductionPerCoreValidState = 2.8409;
  const totalDeductionCarsCoreMechanical = calculateTotalCoreDeductionForTrueStates(carsCoreMechanicalStates, deductionPerCoreValidState);

  function calculateTotalFluidDeductionForValidStates(states, exemptionStates, weight) {
    const valid = states.filter((s) => s && !exemptionStates.includes(s));
    return valid.length * weight;
  }
  const fluidLevelStates = [
    reportData?.isToggledFluidLevel1, reportData?.isToggledFluidLevel2, reportData?.isToggledFluidLevel3,
    reportData?.isToggledFluidLevel4, reportData?.isToggledFluidLevel5, reportData?.isToggledFluidLevel6,
    reportData?.isToggledFluidLevel7, reportData?.isToggledFluidLevel8, reportData?.isToggledFluidLevel9,
    reportData?.isToggledFluidLevel10, reportData?.isToggledFluidLevel11,
  ];
  const exemptFluidLevelStates = [
    reportData?.isToggledFluidLevel1, reportData?.isToggledFluidLevel3, reportData?.isToggledFluidLevel5,
    reportData?.isToggledFluidLevel7,
  ];
  const deductionPerFluidValidState = 2.5;
  const totalDeductionFluidLevel = calculateTotalFluidDeductionForValidStates(fluidLevelStates, exemptFluidLevelStates, deductionPerFluidValidState);

  const initialPercentage = 100;
  const isOdometerConditionMet = reportData?.odometerBasic < reportData?.distKMS ? "Yes" : "No";

  function calculateScorePercentage() {
    const totalDeductions = [
      totalDeductionEngine, totalDeductionSystemObservations, totalDeductionTyres,
      totalDeductionCarsCoreMechanical, totalDeductionFluidLevel,
    ];
    let finalScore = initialPercentage;
    finalScore = totalDeductions.reduce((acc, d) => acc - d, finalScore);
    if (isOdometerConditionMet === "Yes") finalScore -= 0.82;
    return finalScore.toFixed(2);
  }
  const scorePercentage = calculateScorePercentage();

  const treadPercentNum = (raw) => {
    const v = parseFloat(raw);
    if (isNaN(v) || v < 0 || v > 15) return null;
    return Math.min(100, Math.round((v / 9) * 100));
  };

  const scoreEngineTrans = Math.max(0, 100 - totalDeductionEngine - totalDeductionSystemObservations);
  const scoreMechFluids = Math.max(0, 100 - totalDeductionCarsCoreMechanical - totalDeductionFluidLevel);
  const scoreTyres = Math.max(0, 100 - totalDeductionTyres);

  // ---- Zone definitions (same 5 categories the score above is built from) ----

  const coreMechZones = [
    ["HVAC / AC Systems", reportData?.selectedConditionCarsCore1],
    ["Engine Systems", reportData?.selectedConditionCarsCore2],
    ["Brake Systems", reportData?.selectedConditionCarsCore3],
    ["Steering & Suspension Systems", reportData?.selectedConditionCarsCore4],
    ["Fuel Systems", reportData?.selectedConditionCarsCore5],
    ["Battery Systems", reportData?.selectedConditionCarsCore6],
    ["Exhaust Systems", reportData?.selectedConditionCarsCore7],
    ["Filters", reportData?.selectedConditionCarsCore8],
    ["Ignition Systems", reportData?.selectedConditionCarsCore9],
    ["Lighting Systems", reportData?.selectedConditionCarsCore10],
    ["Clutch & Transmission", reportData?.selectedConditionCarsCore11],
    ["Sensors", reportData?.selectedConditionCarsCore12],
  ];

  const systemObsZones = [
    ["Engine Crank", !reportData?.isToggledEngineCrack],
    ["Instrument Cluster", !reportData?.isToggledWarning],
    ["Vibration, Noise, Smell", !reportData?.isToggledSuspicious],
    ["Excessive Smoke From Exhaust", !reportData?.isToggledSmoke],
    ["Gear Shifting", !reportData?.isToggledGearshift],
    ["Pedal Play", !reportData?.isToggledBreak],
    ["Steering Responsiveness", !reportData?.isToggledSteering],
    ["Parking Brake", !reportData?.isToggledHandbreak],
    ["Reverse Gear & Sensor", !reportData?.isToggledReverse],
    ["Speedometer & Tachometer", !reportData?.isToggledSmooth],
  ];

  const fluidZones = [
    ["Coolant", !reportData?.isToggledFluidLevel1 || reportData?.isToggledFluidLevel2],
    ["Engine Oil", !reportData?.isToggledFluidLevel3 || reportData?.isToggledFluidLevel4],
    ["Brake Oil", !reportData?.isToggledFluidLevel5 || reportData?.isToggledFluidLevel6],
    ["Washer Fluid", !reportData?.isToggledFluidLevel7 || reportData?.isToggledFluidLevel8],
    ["Auxiliary Belts", reportData?.isToggledFluidLevel9],
    ["DEF/DPF", reportData?.isToggledFluidLevel10 && reportData?.percentageValue < 50],
    ["Leakages", reportData?.isToggledFluidLevel11],
  ];

  const tyreZones = [
    ["Front Right", reportData?.BrandT, reportData?.BrandTv, reportData?.selectedConditionSTS, reportData?.selectedConditionFT, reportData?.selectedConditionDOTFT, reportData?.BrandTRFR, "tyreFile1"],
    ["Rear Right", reportData?.BrandRT, reportData?.VarientRT, reportData?.selectedConditionRT, reportData?.selectedConditionRTN, reportData?.selectedConditionDOTRT, reportData?.TDRT, "tyreFile2"],
    ["Rear Left", reportData?.BrandRL, reportData?.VarientRL, reportData?.selectedConditionRL, reportData?.selectedConditionRLN, reportData?.selectedConditionDOTRL, reportData?.RLTrend, "tyreFile3"],
    ["Front Left", reportData?.BrandFLN, reportData?.VarientFLN, reportData?.selectedConditionFLN, reportData?.selectedConditionFLNT, reportData?.selectedConditionDOTFLN, reportData?.FLN, "tyreFile4"],
    ["Spare", reportData?.BrandSpare, reportData?.VarientSpare, reportData?.selectedConditionSpare, reportData?.selectedConditionSparen, reportData?.selectedConditionDOTSpare, reportData?.Spare, "tyreFile5"],
  ];

  // ---- Highlights (auto-generated from the same real flags above) ----

  const allChecks = [
    ...coreMechZones.map(([label, val]) => ({ area: label, group: 'Core Mechanical', flagged: !!val, detail: val })),
    ...systemObsZones.map(([label, flagged]) => ({ area: label, group: 'System Observations', flagged: !!flagged })),
    ...fluidZones.map(([label, flagged]) => ({ area: label, group: 'Fluids', flagged: !!flagged })),
    ...tyreZones.map(([label, , , , val]) => ({ area: `${label} Tyre`, group: 'Tyres', flagged: !!val, detail: val })),
  ];
  const needsAttention = allChecks.filter((c) => c.flagged);
  const workingWell = allChecks.filter((c) => !c.flagged);
  const workingWellSample = workingWell.slice(0, 5);
  const needsAttentionSample = needsAttention.slice(0, 5);

  const socialIcons = [
    { src: facebook, href: 'https://www.facebook.com/share/1AhBm5rC7f/' },
    { src: twitter, href: 'https://x.com/i/flow/login?redirect_after_login=%2FCarvetPro' },
    { src: instagram, href: 'https://www.instagram.com/carvetproindia?igsh=MWVraHVydzUydDc5aA==' },
    { src: youtube, href: 'https://youtube.com/@carvetproindia?si=LvAdbNabFiNvC8Pc' },
  ];
  const footerServices = [
    ['Pre Delivery Inspection', 'https://carvetpro.com/service?inspectionType=Pre%20Delivery%20Inspection'],
    ['Used Car Inspection', 'https://carvetpro.com/service?inspectionType=Used%20Car%20Inspections'],
    ['Pre-Trip Inspection', 'https://carvetpro.com/service?inspectionType=Pre%20Trip%20Inspections'],
    ['Driver Assistance', null],
  ];
  const currentYear = new Date().getFullYear();

  return (
  <Document>

    {/* ================= PAGE 1 — COVER ================= */}
    <Page size="A4" style={styles.darkPage}>
      <View style={{ paddingHorizontal: 40, paddingTop: 50 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 40 }}>
          <Image style={{ width: 130, height: 38 }} src={logo} />
        </View>
        <Text style={{ fontFamily: 'Clash', fontSize: 40, color: '#FFFFFF', marginBottom: 8 }}>
          Pre-Trip Inspection Report
        </Text>
        <Text style={{ fontSize: 11, color: LIME_TINT, marginBottom: 24 }}>
          Route-ready checks before every long drive, so breakdowns don't catch you off guard.
        </Text>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 30 }}>
          {[
            'Experienced Evaluators',
            'Mechanical Readiness Focus',
            'Deduction-Based Scoring',
            'Transparent, Field-by-Field Reporting',
          ].map((t) => (
            <View key={t} style={{
              backgroundColor: 'rgba(173,209,68,0.12)', borderWidth: 1, borderColor: LIME,
              borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, marginRight: 10, marginBottom: 10,
            }}>
              <Text style={{ fontSize: 9, color: LIME_TINT, fontFamily: 'PoppinsS' }}>{t}</Text>
            </View>
          ))}
        </View>

        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
          <Text style={{ fontSize: 10, color: '#FFFFFF' }}>
            {serviceData?.address ? serviceData.address.split(',').slice(-1)[0].trim() : 'Bangalore'}
          </Text>
          <Text style={{ fontSize: 10, color: LIME, marginHorizontal: 8 }}>|</Text>
          <Text style={{ fontSize: 10, color: '#FFFFFF' }}>
            {reportData?.pdiDate ? reportData.pdiDate : new Date().toLocaleDateString('en-IN')}
          </Text>
        </View>
      </View>

      <View style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 460 }}>
        <Image src={carImageUrl || car} style={{ width: '100%', height: 460, objectFit: 'cover', objectPosition: 'bottom', opacity: 0.85 }} />
        <Svg width="595" height="460" style={{ position: 'absolute', top: 0, left: 0 }}>
          <Defs>
            <LinearGradient id="coverFade" x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0" stopColor={DARK} stopOpacity="1" />
              <Stop offset="0.25" stopColor={DARK} stopOpacity="0.1" />
              <Stop offset="1" stopColor={DARK} stopOpacity="0.4" />
            </LinearGradient>
          </Defs>
          <Rect x="0" y="0" width="595" height="460" fill="url(#coverFade)" />
        </Svg>
      </View>
    </Page>

    {/* ================= PAGE 2 — OVERVIEW =================
        Same VIN-based identity block as PDI (not Used Car's registration/
        chassis/ownership) — this vehicle already went through a PDI, it
        has a VIN. Fields traced directly from the pre-redesign
        preTripPdf.jsx's own Overview card: Booking ID, PDI Date,
        Conducted By, Time, Dealer, VIN, Transmission, Fuel, Engine
        Number, Engine Type, Emission Type, Keys — no Allotted VIN/Dealer
        Type/Vehicle Type/Delivery Date (never existed in this report;
        those are PDI/Used-Car-only fields tied to a dealer handover this
        report doesn't have). Service Type and Customer Phone added from
        the start, matching the fix already applied to PDI and Used Car. */}
    <Page size="A4" style={styles.page}>
      <PageHeader />
      <View style={styles.body}>
        <Text style={styles.h1}>Overview</Text>
        <Text style={{ fontSize: 10, color: TEXT_LABEL, marginBottom: 12 }}>
          Prepared for {serviceData?.customerName || 'N/A'}
        </Text>

        <View style={[styles.row, { marginBottom: 16 }]}>
          <Image style={{ width: 150, height: 110, borderRadius: 10, marginRight: 16 }} src={carImageUrl || car} />
          <View style={{ flex: 1 }}>
            <Text style={{ fontFamily: 'Clash', fontSize: 18, color: DARK }}>
              {serviceData?.vehicleMake && serviceData?.vehicleModel ? `${serviceData.vehicleMake} ${serviceData.vehicleModel}` : 'N/A'}
            </Text>
            <Text style={{ fontSize: 10, color: TEXT_LABEL, marginTop: 2, marginBottom: 10 }}>
              {serviceData?.variant || 'N/A'}{reportData?.transmission ? ` | ${reportData.transmission}` : ''}
            </Text>
            <View style={styles.divider} />
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ fontSize: 10, color: TEXT_LABEL }}>Overall Reliability Score</Text>
              <View style={[styles.badge, { backgroundColor: tierFromScore(Number(scorePercentage)).bg }]}>
                <Text style={{ fontSize: 13, fontFamily: 'PoppinsS', color: tierFromScore(Number(scorePercentage)).color }}>
                  {scorePercentage}% · {tierLabelTitleCase(Number(scorePercentage))}
                </Text>
              </View>
            </View>
            <Text style={{ fontSize: 8, color: TEXT_LABEL, marginTop: 6 }}>
              {scoreDescription(Number(scorePercentage))}
            </Text>
          </View>
        </View>

        <View style={[styles.cardGray, { flexDirection: 'row' }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>BHP</Text>
            <Text style={styles.value}>{serviceData?.bhp || 'N/A'}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Airbags</Text>
            <Text style={styles.value}>{serviceData?.airbags || 'N/A'}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Mileage (kmpl)</Text>
            <Text style={styles.value}>{serviceData?.mileage || 'N/A'}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>NCAP</Text>
            <Text style={styles.value}>{serviceData?.ncap || 'N/A'}</Text>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={[styles.h2, { fontFamily: 'PoppinsS', fontSize: 17, color: '#000000' }]}>Vehicle &amp; Booking Details</Text>
          <View style={styles.grid2}>
            {[
              ['Booking ID', shortBookingId(serviceData?._id)],
              ['Service Type', serviceData?.inspectionType],
              ['Customer Phone', serviceData?.customerPhone],
              ['PDI Date', reportData?.pdiDate || new Date().toLocaleDateString('en-IN')],
              ['Time', reportData?.pdiTime || new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })],
              ['Conducted By', reportData?.technicianName],
              ['VIN', reportData?.vinNumber],
              ['Engine Number', reportData?.engineNumber],
              ['Engine Type', serviceData?.engine],
              ['Fuel', serviceData?.fuel],
              ['Emission Standard', reportData?.emissionType],
              ['Transmission', reportData?.transmission],
              ['Keys', reportData?.keysBasic],
              ['Dealer Showroom', reportData?.selectedButton ? 'Done' : 'N/A'],
            ].map(([l, v]) => (
              <View style={styles.gridCell} key={l}>
                <Text style={styles.label}>{l}</Text>
                <Text style={styles.value}>{v || 'N/A'}</Text>
              </View>
            ))}
          </View>

          <View style={{ marginTop: 4 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text style={{ fontFamily: 'PoppinsS', fontSize: 10, color: DARK }}>Odometer Consistency Check</Text>
              <Badge tier={isOdometerConditionMet === 'Yes' ? STATUS.attention : STATUS.excellent} />
            </View>
            {isOdometerConditionMet === 'Yes' ? (
              <View style={styles.reasonBox}>
                <Text style={styles.reasonLine}>
                  <Text style={styles.reasonLabel}>Reason: </Text>
                  <Text style={styles.reasonText}>
                    {`Current ODO: ${reportData?.odometerBasic ?? 'N/A'} km · Recorded vehicle usage: ${reportData?.distKMS ?? 'N/A'} km. The odometer reading is lower than recorded usage — possible tampering or a data-entry mismatch, worth confirming before departure.`}
                  </Text>
                </Text>
              </View>
            ) : (
              <Text style={{ fontSize: 9, color: TEXT_BODY }}>
                {`Current ODO: ${reportData?.odometerBasic ?? 'N/A'} km · Recorded vehicle usage: ${reportData?.distKMS ?? 'N/A'} km. No discrepancy found.`}
              </Text>
            )}
          </View>
        </View>

        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>What Needs Attention</Text>
        <Text style={styles.subtitle}>Areas flagged by the technician that may require attention before this trip</Text>
        {needsAttentionSample.length === 0 && <Text style={{ fontSize: 9, color: TEXT_LABEL }}>No issues were flagged during this inspection.</Text>}
        {needsAttentionSample.map((c, i) => (
          <View key={i} wrap={false} style={[styles.highlightRow, { backgroundColor: '#FFF3E8' }]}>
            <View style={[styles.highlightDot, { backgroundColor: '#D97706' }]} />
            <Text style={styles.highlightText}>{c.area}{c.detail ? ` — ${c.detail}` : ' has an issue flagged.'}</Text>
          </View>
        ))}
        {needsAttention.length > needsAttentionSample.length && (
          <Text style={{ fontSize: 8, color: TEXT_LABEL, marginTop: 4 }}>
            +{needsAttention.length - needsAttentionSample.length} more flagged item(s) — see the detailed sections that follow.
          </Text>
        )}

        <TechnicianRemarks reportData={reportData} />
        </View>

        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Inspection Overview</Text>
        <Text style={styles.subtitle}>Category scores, derived from the same deduction totals used for the overall reliability score</Text>

        {[
          ['Engine & System Observations', scoreEngineTrans, systemObsZones.filter(z => z[1]).length + (enginePerformance.some(Boolean) ? 1 : 0), systemObsZones.length + enginePerformance.length],
          ['Core Mechanical & Fluids', scoreMechFluids, coreMechZones.filter(z => z[1]).length + fluidZones.filter(z => z[1]).length, coreMechZones.length + fluidZones.length],
          ['Tyres', scoreTyres, tyreZones.filter(z => z[4]).length, tyreZones.length],
        ].map(([name, score, flaggedCount, total]) => {
          const tier = tierFromScore(score);
          return (
            <View style={styles.scoreCard} key={name} wrap={false}>
              <View style={styles.scoreCardHeadRow}>
                <Text style={styles.scoreCardTitle}>{name}</Text>
                <Badge tier={tier} />
              </View>
              <Text style={styles.scoreCardDesc}>
                {flaggedCount === 0
                  ? 'All checks in this category are in good condition.'
                  : 'Some checks in this category are flagged for attention.'}
              </Text>
            </View>
          );
        })}

        {[reportData?.basicFile1, reportData?.basicFile2, reportData?.basicFile3, reportData?.basicFile4].some(Boolean) && (
          <View style={{ marginTop: 20 }}>
            <Text style={styles.h1}>Vehicle Inspection Gallery</Text>
            <Text style={styles.subtitle}>Hero angles captured during inspection</Text>
            <View style={styles.photoGrid}>
              <PhotoCard src={reportData?.basicFile1} caption="Front View" wide />
              <PhotoCard src={reportData?.basicFile2} caption="Rear View" wide />
              <PhotoCard src={reportData?.basicFile3} caption="Side Profile" wide />
              <PhotoCard src={reportData?.basicFile4} caption="Side Profile" wide />
            </View>
          </View>
        )}
        </View>

        {/* ================= DETAILED INSPECTION FINDINGS =========
            5 categories, matching what the live pre-redesign report actually
            checked and scored — Engine Performance, System Observations,
            Core Mechanical, Fluid Inspection, Tyres. Body & Paint / Panel Gap
            / Interior / Infotainment were commented out of both the display
            and the score in production before this redesign (confirmed via
            the pre-redesign source, not assumed) — kept out here too, per
            explicit decision: this is a presentation redesign, not the
            moment to silently expand what Pre-Trip actually checks.
            No forced page break above this comment (Rule 1) — this
            continues in the same <Page>/body as the Gallery above so
            Engine & Transmission can flow onto the same physical page
            if there's room left after the gallery's last photo. */}
        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Engine &amp; Transmission</Text>
        <Text style={styles.subtitle}>Real-time parameter evaluation and driver-facing system observations</Text>

        <View style={styles.card}>
          <Text style={styles.h2}>Real-Time Parameter Evaluation (Idle)</Text>
          {[
            ['Battery Voltage (v)', reportData?.BatteryEnergy, reportData?.BatteryEnergy < 11.5],
            ['Engine Load (%)', reportData?.LoadEngine, reportData?.LoadEngine > 35],
            ['Idle RPM', reportData?.idleRPM, reportData?.idleRPM > 1400],
          ].map(([label, val, flagged]) => (
            <ZoneRow key={label} label={`${label}: ${val ?? 'N/A'}`} value={flagged ? 'Outside expected range' : ''} flagged={!!flagged} />
          ))}
          {Array.isArray(reportData?.errorCodes) && reportData.errorCodes.length > 0 && (
            <Text style={{ fontSize: 8.5, color: '#D97706', marginTop: 8 }}>
              OBD Error Codes: {reportData.errorCodes.join(', ')}
            </Text>
          )}
        </View>

        <View style={styles.card}>
          <Text style={styles.h2}>System Observations</Text>
          <ZoneGrid zones={systemObsZones} />
        </View>

        <Legend2 />
        </View>

        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Core Mechanical System Check</Text>
        <Text style={styles.subtitle}>Steering, suspension, brakes, and the vehicle's core mechanical systems</Text>

        {/* Not wrap={false}: this card holds the full Core Mechanical zone
            grid and is tall — forcing the whole thing to stay together made
            it jump to the next page and strand the heading above with a big
            gap. ZoneGrid keeps each row-pair intact on its own (row-level
            wrap={false}), so the card can split at a row boundary and the
            heading stays with its first rows. Matches PDI / Used Car. */}
        <View style={styles.card}>
          <ZoneGrid zones={coreMechZones} />
        </View>

        <View style={styles.card}>
          <Text style={styles.h2}>Fluid Inspection</Text>
          <ZoneGrid zones={fluidZones} />
        </View>

        <Legend2 />

        <View style={{ marginTop: 20 }}>
          <Text style={styles.h1}>Tyres</Text>
          <Text style={styles.subtitle}>Detailed examination of tyre health, including tread depth and overall wear</Text>

          <View style={styles.photoGrid}>
            {tyreZones.map(([label, brand, variant, size, condition, dot, treadRaw, fileKey]) => {
              const pct = treadPercentNum(treadRaw);
              return (
                <View wrap={false} style={styles.photoCell3} key={label}>
                  {reportData?.[fileKey] && <Image style={styles.photoImg} src={reportData[fileKey]} />}
                  <Text style={styles.photoCaption}>{label} Tyre</Text>
                  <Text style={{ fontSize: 8, color: TEXT_LABEL, marginTop: 2 }}>
                    {pct !== null ? `${pct}% Tread Remaining` : (treadRaw ? `Raw reading: ${treadRaw}` : 'No reading recorded')}
                  </Text>
                </View>
              );
            })}
          </View>

          <View style={styles.card}>
            {tyreZones.map(([label, brand, variant, size, condition, dot]) => (
              <ZoneRow
                key={label}
                label={label}
                value={`Brand: ${brand || 'N/A'} · Variant: ${variant || 'N/A'} · Size: ${size || 'N/A'} · DOT/Mfg: ${dot || 'N/A'}`}
                reason={condition || ''}
                flagged={!!condition}
              />
            ))}
          </View>

          <View wrap={false} style={styles.footnote}>
            <Text style={styles.footnoteText}>
              Tread % is estimated against a 9mm new-tyre reference depth (consistent with CarvetPro's existing tread-depth convention); actual new-tyre depth varies by model. &gt;80% good life remaining, &lt;20% replacement needed.
            </Text>
          </View>
        </View>
        </View>
      </View>
    </Page>

    {/* ================= PAGE — CLOSING ================= */}
    <Page size="A4" style={styles.darkPage}>
      <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'space-between' }}>
        <View style={{ paddingHorizontal: 40, paddingTop: 70 }}>
          <Text style={{ fontFamily: 'Clash', fontSize: 40, color: '#FFFFFF', marginBottom: 8 }}>Thank You</Text>
          <Text style={{ fontSize: 13, color: LIME_TINT, marginBottom: 40 }}>for choosing CarvetPro</Text>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {[
              ['Pre Delivery Inspection', 'Thorough pre-delivery checks so you take ownership with total confidence.', 'https://carvetpro.com/service?inspectionType=Pre%20Delivery%20Inspection'],
              ['Used Car Inspection', 'A 350-point evaluation before you buy, so surprises stay off the road.', 'https://carvetpro.com/service?inspectionType=Used%20Car%20Inspections'],
              ['Pre-Trip Inspection', 'Route-ready checks before every long drive, so breakdowns don’t catch you off guard.', 'https://carvetpro.com/service?inspectionType=Pre%20Trip%20Inspections'],
              ['Driver Assistance', 'On-call technical support to keep you moving, wherever the trip takes you.', 'https://carvetpro.com/contact?type=Driver%20Assistance'],
            ].map(([title, desc, href]) => (
              <Link key={title} src={href} style={{ width: '48%', marginRight: '2%', marginBottom: 20, backgroundColor: 'rgba(173,209,68,0.08)', borderRadius: 10, padding: 24, borderWidth: 1, borderColor: 'rgba(173,209,68,0.3)', textDecoration: 'none' }}>
                <Text style={{ fontFamily: 'PoppinsS', fontSize: 13, color: '#FFFFFF', marginBottom: 8 }}>{title}</Text>
                <Text style={{ fontSize: 9, color: '#D8D8D8', lineHeight: 1.4 }}>{desc}</Text>
              </Link>
            ))}
          </View>

          <Text style={{ fontSize: 9.5, color: '#9AA37A', marginTop: 10 }}>
            Questions about this report or your vehicle? Reach us anytime — details below.
          </Text>
        </View>

        <View style={{ paddingHorizontal: 40, flexDirection: 'row', flexWrap: 'wrap' }}>
          {[
            'Experienced Evaluators',
            'Mechanical Readiness Focus',
            'Deduction-Based Scoring',
            'Transparent, Field-by-Field Reporting',
          ].map((t) => (
            <View key={t} style={{
              backgroundColor: 'rgba(173,209,68,0.12)', borderWidth: 1, borderColor: LIME,
              borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, marginRight: 10, marginBottom: 10,
            }}>
              <Text style={{ fontSize: 9, color: LIME_TINT, fontFamily: 'PoppinsS' }}>{t}</Text>
            </View>
          ))}
        </View>

        <View style={styles.footerContainer}>
          <View style={styles.contentWrapper}>
            <View style={styles.sectionss}>
              <Image src={footerLogo} style={styles.footerLogo} />
              <Text style={styles.description}>Your trusted partner for expert vehicle inspections and road-ready confidence.</Text>
              <View style={styles.socialIcons}>
                {socialIcons.map((icon, index) => (
                  <Link key={index} src={icon.href} style={styles.iconWrapper}>
                    <Image src={icon.src} style={styles.socialIcon} />
                  </Link>
                ))}
              </View>
            </View>
            <View style={styles.sectionss}>
              <Text style={styles.sectionHeading}>Our Services</Text>
              {footerServices.map(([label, href]) => (
                href
                  ? <Link key={label} src={href} style={[styles.listItem, { color: '#EDEDED', textDecoration: 'none' }]}>{label}</Link>
                  : <Text key={label} style={styles.listItem}>{label}</Text>
              ))}
            </View>
            <View style={styles.sectionss}>
              <Text style={styles.sectionHeading}>Contact Us</Text>
              <Link src="mailto:info@carvetpro.com" style={[styles.listItem, { color: '#EDEDED', textDecoration: 'none' }]}>info@carvetpro.com</Link>
              <Text style={styles.listItem}>+91 96115 60076</Text>
              <Text style={styles.listItem}>Bangalore, India 560100</Text>
            </View>
          </View>
          <View style={styles.dividers} />
          <Text style={styles.copyright}>CARVETPRO © All rights reserved. Copyright {currentYear}</Text>
        </View>
      </View>
    </Page>

    {/* ================= PAGE — DISCLAIMER / T&C ================= */}
    <Page size="A4" style={styles.darkPage} wrap>
      <View style={{ paddingHorizontal: 36, paddingTop: 28 }}>
        <Text style={{ fontFamily: 'PoppinsS', fontSize: 14, color: '#FFFFFF', marginBottom: 8 }}>Disclaimer</Text>
        <Text style={{ fontSize: 7.5, color: '#CFCFCF', lineHeight: 1.5, marginBottom: 12 }}>
          This Inspection Report reflects the condition of the vehicle as observed and recorded by CarvetPro's evaluator at the time of inspection, to the best of their ability, using visual and functional checks and — where stated in this Report — basic diagnostic-tool readings (e.g. OBD parameters, tyre tread gauge). It does not involve dismantling components, paint-depth/thickness gauging, or laboratory-grade diagnostics unless explicitly stated. This Report focuses on mechanical readiness for the road (engine, core mechanical systems, fluids, tyres) and does not cover cosmetic body, paint, or interior condition. Vehicle condition can change after the inspection due to usage, weather, repairs, tampering, or defects that were not visible or detectable at the time of inspection. This Report is provided for informational purposes only and is not a warranty, certification, valuation, or guarantee of the vehicle's performance, condition, marketability, or fitness for any particular purpose. By relying on this Report, the customer agrees that CarvetPro (operated by the entity providing services at carvetpro.com), its employees, technicians, and affiliates are not liable for direct, indirect, incidental, or consequential claims, losses, depreciation, damages, or liabilities arising from the use, interpretation, or reliance on this Report, except as required by applicable Indian law.
        </Text>

        <Text style={{ fontFamily: 'PoppinsS', fontSize: 14, color: '#FFFFFF', marginBottom: 10 }}>Terms &amp; Conditions for Inspection Report</Text>
        {[
          ['1. Services Covered', 'CarvetPro provides vehicle inspection and related services: Pre-Delivery Inspection (PDI) of new vehicles before handover, Used Car Inspection prior to purchase, Pre-Trip Inspection before long journeys, and Driver Assistance support. This Report documents the findings of the specific service type stated on its cover page, performed on the date and at the odometer reading recorded therein.'],
          ['2. Nature of Assessment', 'The inspection is a point-in-time visual and functional assessment performed by a CarvetPro-affiliated technician, focused on mechanical readiness for the road — engine performance, core mechanical systems, fluid levels, and tyres. It does not cover cosmetic body, paint, or interior condition, does not involve dismantling parts or invasive testing unless explicitly stated, and cannot reveal hidden, latent, intermittent, or internal defects not apparent through visual/functional checks.'],
          ['3. No Warranty, Certification, or Valuation', 'This Report is not a certification, endorsement, warranty, or guarantee of the vehicle\'s condition, safety, performance, or future reliability, and is not a valuation or price recommendation. No statement in this Report, written or implied, constitutes a representation or warranty by CarvetPro regarding the vehicle inspected.'],
          ['4. Customer Reliance and Independent Verification', 'Reliance on this Report is at the customer\'s discretion. CarvetPro recommends that customers relying on this Report before a long drive also seek independent verification — including an independent mechanical inspection or a diagnostic scan — particularly for defects noted as needing attention.'],
          ['5. Limitation of Liability', 'To the maximum extent permitted under Indian law, CarvetPro, its officers, employees, technicians, and affiliates are not liable for defects, mechanical or electronic failures, depreciation, accidents, or losses arising after the inspection date, or for conditions not reasonably detectable through the visual/functional assessment described in Section 2. This limitation does not exclude liability that cannot lawfully be excluded, including liability for fraud or gross negligence.'],
          ['6. Customer Disclosure Obligations', 'The customer (or the party requesting the inspection) is responsible for disclosing known issues, prior accidents, insurance claims, or modifications relevant to the vehicle before the inspection. CarvetPro is not responsible for omissions or misrepresentations made by the customer or any third party involved.'],
          ['7. Data Collection, Photographs & Privacy', 'In the course of this inspection, CarvetPro collected vehicle information (registration, VIN/engine numbers, odometer reading), inspection photographs, and the contact details of the person who booked the service, storing them securely (including via encrypted cloud storage) to generate and deliver this Report and to maintain service records. This data is processed in accordance with India\'s Digital Personal Data Protection Act, 2023, is not sold to third parties, and is retained only as long as reasonably needed for service delivery, support, and legal/record-keeping purposes. Questions or grievances regarding personal data in this Report can be directed to CarvetPro at info@carvetpro.com.'],
          ['8. Intellectual Property', 'This Report, including its layout, scoring methodology, and photographs, is proprietary to CarvetPro and is provided for the customer\'s personal reference in connection with the inspected vehicle. It may not be reproduced, resold, or represented as an independent third-party certification without CarvetPro\'s written consent.'],
          ['9. Governing Law & Dispute Resolution', 'These Terms are governed by the laws of India. Any dispute arising from this Report or the underlying inspection service shall first be raised with CarvetPro directly at info@carvetpro.com for resolution; unresolved disputes shall be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.'],
          ['10. Changes to These Terms', 'CarvetPro may update these Terms from time to time for future inspections; the version applicable to this Report is the one in effect on the inspection date stated on its cover page.'],
          ['11. Acceptance', 'Booking, receiving, downloading, or relying on this Report constitutes acceptance of these Terms & Conditions.'],
        ].map(([h, b], i, arr) => (
          <View key={h} style={{ marginBottom: 8 }} wrap={i < arr.length - 1 ? false : undefined}>
            <Text style={{ fontSize: 8.5, fontFamily: 'PoppinsS', color: LIME_TINT, marginBottom: 2 }}>{h}</Text>
            <Text style={{ fontSize: 7.5, color: '#CFCFCF', lineHeight: 1.4 }}>{b}</Text>
          </View>
        ))}
      </View>
    </Page>

  </Document>
  );
};

// react-pdf's Image component (used for PhotoCard below) is defined at the
// top of this file — declared after the main component only because it
// wasn't needed until here; PhotoCard itself is a plain function component.
function PhotoCard({ src, caption, wide }) {
  if (!src) return null;
  return (
    <View wrap={false} style={wide ? styles.photoCell : styles.photoCell3}>
      <Image style={styles.photoImg} src={src} />
      <Text style={styles.photoCaption}>{caption}</Text>
    </View>
  );
}

// Technician's Remarks — numbered free-text lines, each with an optional
// evidence photo the technician attached to that specific line. Reads the
// structured `technicianRemarks` array ([{ text, photoUrl }]) when present
// (server normalises older reports into it from the plain-text
// `finalObservations`); falls back to splitting `finalObservations` itself
// so a freshly-generated report that only carries the string still renders.
function remarksFromReportData(reportData) {
  const arr = reportData?.technicianRemarks;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr
      .filter((r) => r && (typeof r.text === 'string' || r.photoUrl))
      .map((r) => ({ text: (r.text || '').trim(), photoUrl: r.photoUrl || null }))
      .filter((r) => r.text || r.photoUrl);
  }
  return String(reportData?.finalObservations || '')
    .split('\n')
    .map((line) => ({ text: line.trim(), photoUrl: null }))
    .filter((r) => r.text);
}

function TechnicianRemarks({ reportData }) {
  const remarks = remarksFromReportData(reportData);
  if (remarks.length === 0) return null;
  return (
    <View style={[styles.card, { marginTop: 14 }]}>
      <Text style={styles.h2}>Technician's Remarks</Text>
      {remarks.map((r, index) => (
        <View
          key={index}
          wrap={false}
          style={[styles.remarkRow, index === remarks.length - 1 && styles.remarkRowLast]}
        >
          <Text style={styles.remarkText}>{r.text}</Text>
          {!!r.photoUrl && <Image style={styles.remarkPhoto} src={r.photoUrl} />}
        </View>
      ))}
    </View>
  );
}

export default PreTripPDF;
